/** The case list, and creating one — which is where the purpose gate lives. */

import { useState, type FormEvent, type ReactElement, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { request } from '@/lib/api';
import type {
  CaseResponse,
  CreateCaseRequest,
  InvestigationPurpose,
  LegalBasis,
  Paged,
} from '@/lib/schemas';
import { Badge, Button, Card, ErrorState, Loading, Table } from '@/components/ui';
import { useCan } from '@/hooks/usePermissions';

/**
 * Purposes the platform will run an investigation for.
 *
 * `employment`, `credit`, `insurance` and `housing` are **absent on purpose**: ADR-0024
 * blocks them because the FCRA makes them regulated consumer reports, and this platform
 * is not a consumer reporting agency. The backend refuses them by name and the database
 * refuses them again; not offering them is how an analyst learns that before typing an
 * attestation.
 *
 * Typed from the schema, so a value the API would reject fails to compile. This was kept
 * in step by hand, on the reasoning that the API answers 422 and a drift is therefore a bad
 * select rather than a bad case — true, but it made the person who picked the option find
 * out instead of the person who wrote it.
 */
const PURPOSES: { value: InvestigationPurpose; label: string }[] = [
  { value: 'due_diligence', label: 'Due diligence' },
  { value: 'kyc', label: 'Know your customer' },
  { value: 'fraud_investigation', label: 'Fraud investigation' },
  { value: 'cybersecurity', label: 'Cybersecurity' },
  { value: 'journalism', label: 'Journalism' },
  { value: 'brand_protection', label: 'Brand protection' },
  { value: 'self_footprint', label: 'My own footprint' },
];

const LEGAL_BASES: { value: LegalBasis; label: string }[] = [
  { value: 'legitimate_interest', label: 'Legitimate interest' },
  { value: 'legal_obligation', label: 'Legal obligation' },
  { value: 'contract', label: 'Contract' },
  { value: 'consent', label: 'Consent' },
];

const STATUSES = ['draft', 'running', 'review', 'complete', 'archived'];

/** Identifier types the domain accepts. Mirrors `SubjectInputModel` on the API. */
const SUBJECT_TYPES = [
  { value: 'full_name', label: 'Full name' },
  { value: 'email', label: 'Email' },
  { value: 'username', label: 'Username' },
  { value: 'domain', label: 'Domain' },
  { value: 'url', label: 'URL' },
  { value: 'orcid', label: 'ORCID' },
  { value: 'phone', label: 'Phone' },
  { value: 'company', label: 'Company' },
];

/** The case list view. */
export function CasesPage(): ReactElement {
  const canCreate = useCan('create_run_cases');
  const [creating, setCreating] = useState(false);
  const [status, setStatus] = useState('');
  const [purpose, setPurpose] = useState('');
  const [sort, setSort] = useState('-created_at');

  // **Real cursor pagination.** The server used to return the first page of an in-memory
  // slice with `next_cursor: null`, so a tenant with more cases than one page could never
  // reach the rest. `useInfiniteQuery` follows the cursor the API now issues.
  const cases = useInfiniteQuery({
    queryKey: ['cases', status, purpose, sort],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      request<Paged<CaseResponse>>('/cases', {
        query: {
          limit: 25,
          cursor: pageParam,
          status: status || undefined,
          purpose: purpose || undefined,
          sort,
        },
      }),
    getNextPageParam: (last) => last.page.next_cursor ?? undefined,
  });

  if (cases.isLoading) return <Loading />;
  if (cases.error) return <ErrorState error={cases.error} />;

  const rows = cases.data?.pages.flatMap((page) => page.data) ?? [];

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between">
        <h1 className="text-lg font-semibold">Cases</h1>
        {canCreate && (
          <Button tone="primary" onClick={() => setCreating((open) => !open)}>
            {creating ? 'Cancel' : 'New case'}
          </Button>
        )}
      </header>

      {creating && <CreateCaseForm onCreated={() => setCreating(false)} />}

      <Card>
        <div className="flex flex-wrap items-end gap-3 p-3">
          <Filter label="Status" value={status} onChange={setStatus} testId="filter-status">
            <option value="">Any status</option>
            {STATUSES.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </Filter>
          <Filter label="Purpose" value={purpose} onChange={setPurpose} testId="filter-purpose">
            <option value="">Any purpose</option>
            {PURPOSES.map((entry) => (
              <option key={entry.value} value={entry.value}>
                {entry.label}
              </option>
            ))}
          </Filter>
          <Filter label="Sort" value={sort} onChange={setSort} testId="filter-sort">
            <option value="-created_at">Newest first</option>
            <option value="created_at">Oldest first</option>
          </Filter>
        </div>
      </Card>

      <Table
        headers={['Reference', 'Title', 'Purpose', 'Status', 'Subjects']}
        empty="No cases match these filters."
        rows={rows.map((entry) => [
          <Link key="ref" to={`/cases/${entry.id}`} className="font-mono text-accent">
            {entry.reference}
          </Link>,
          entry.title,
          <Badge key="purpose">{entry.investigation_purpose}</Badge>,
          <Badge key="status" tone={entry.status === 'archived' ? 'muted' : 'accent'}>
            {entry.status}
          </Badge>,
          entry.subject_count,
        ])}
      />

      {cases.hasNextPage && (
        <Button
          data-testid="load-more-cases"
          disabled={cases.isFetchingNextPage}
          onClick={() => void cases.fetchNextPage()}
        >
          {cases.isFetchingNextPage ? 'Loading…' : 'Load more'}
        </Button>
      )}
    </div>
  );
}

/**
 * One labelled filter control.
 *
 * @param props - Label, current value, change handler and options.
 * @returns The control.
 */
function Filter({
  label,
  value,
  onChange,
  testId,
  children,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  testId: string;
  children: ReactNode;
}): ReactElement {
  return (
    <label className="block text-xs">
      <span className="text-muted">{label}</span>
      <select
        data-testid={testId}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="mt-1 block rounded border border-line bg-surface px-2 py-1.5 text-sm"
      >
        {children}
      </select>
    </label>
  );
}

/** Creating a case: purpose, legal basis and a written attestation, all required. */
function CreateCaseForm({ onCreated }: { onCreated: () => void }): ReactElement {
  const queryClient = useQueryClient();
  const [title, setTitle] = useState('');
  const [purpose, setPurpose] = useState(PURPOSES[0]!.value);
  const [legalBasis, setLegalBasis] = useState(LEGAL_BASES[0]!.value);
  const [attestation, setAttestation] = useState('');
  const [identifier, setIdentifier] = useState('');
  const [identifierType, setIdentifierType] = useState('full_name');

  const create = useMutation({
    mutationFn: () =>
      request<CaseResponse, CreateCaseRequest>('/cases', {
        method: 'POST',
        body: {
          title,
          investigation_purpose: purpose,
          legal_basis: legalBasis,
          authorization_attestation: attestation,
          // No `is_primary`: `SubjectInputModel` has no such field, so the API ignored
          // it. The first subject of a new case is marked primary server-side anyway.
          subjects: [{ identifier_type: identifierType, identifier_value: identifier }],
        },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['cases'] });
      onCreated();
    },
  });

  function onSubmit(event: FormEvent): void {
    event.preventDefault();
    create.mutate();
  }

  return (
    <Card title="New case">
      <form onSubmit={onSubmit} className="space-y-3">
        <label className="block text-sm">
          <span className="text-muted">Title</span>
          <input
            required
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            className="mt-1 w-full rounded border border-line bg-surface px-2 py-1.5 text-sm"
          />
        </label>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block text-sm">
            <span className="text-muted">Investigation purpose</span>
            <select
              value={purpose}
              onChange={(event) => setPurpose(event.target.value as InvestigationPurpose)}
              className="mt-1 w-full rounded border border-line bg-surface px-2 py-1.5 text-sm"
            >
              {PURPOSES.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-sm">
            <span className="text-muted">Legal basis</span>
            <select
              value={legalBasis}
              onChange={(event) => setLegalBasis(event.target.value as LegalBasis)}
              className="mt-1 w-full rounded border border-line bg-surface px-2 py-1.5 text-sm"
            >
              {LEGAL_BASES.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        </div>
        {/* **The type is chosen, not assumed.** This field recorded every subject as a
            `full_name` whatever was typed into it, so an email address reached none of
            the six email-capable sources and the run collected nothing. Further subjects
            are added from the case's Subjects tab afterwards. */}
        <div className="flex flex-wrap items-end gap-3">
          <label className="block text-sm">
            <span className="text-muted">Subject type</span>
            <select
              data-testid="new-case-subject-type"
              value={identifierType}
              onChange={(event) => setIdentifierType(event.target.value)}
              className="mt-1 block rounded border border-line bg-surface px-2 py-1.5 text-sm"
            >
              {SUBJECT_TYPES.map((entry) => (
                <option key={entry.value} value={entry.value}>
                  {entry.label}
                </option>
              ))}
            </select>
          </label>
          <label className="block flex-1 text-sm">
            <span className="text-muted">Subject</span>
            <input
              data-testid="new-case-subject"
              required
              value={identifier}
              onChange={(event) => setIdentifier(event.target.value)}
              className="mt-1 w-full rounded border border-line bg-surface px-2 py-1.5 text-sm"
            />
          </label>
        </div>
        <label className="block text-sm">
          <span className="text-muted">
            Authorisation attestation — what engagement permits this investigation
          </span>
          <textarea
            required
            minLength={20}
            rows={3}
            value={attestation}
            onChange={(event) => setAttestation(event.target.value)}
            className="mt-1 w-full rounded border border-line bg-surface px-2 py-1.5 text-sm"
          />
          <span className="mt-1 block text-xs text-muted">
            Recorded immutably against the case and shown on every export. Employment,
            credit, insurance and housing decisions are not offered: those produce
            regulated consumer reports (ADR-0024).
          </span>
        </label>
        {create.error != null && <ErrorState error={create.error} />}
        <Button type="submit" tone="primary" disabled={create.isPending}>
          {create.isPending ? 'Creating…' : 'Create case'}
        </Button>
      </form>
    </Card>
  );
}
